import { PdpLoggerApi } from '@internal/plugin-pdp-logger-frontend';

// Dynatrace-aware fetch wrapper for PDP
function sanitizeHeaders(headers?: RequestInit['headers']) {
  if (!headers) return undefined;
  // Convert Headers object or array to plain object
  let plainHeaders: Record<string, string> = {};
  if (headers instanceof Headers) {
    headers.forEach((value, key) => {
      plainHeaders[key] = value;
    });
  } else if (Array.isArray(headers)) {
    headers.forEach(([key, value]) => {
      plainHeaders[key] = value;
    });
  } else if (typeof headers === 'object') {
    plainHeaders = { ...headers } as Record<string, string>;
  }
  // Mask sensitive headers
  for (const key of Object.keys(plainHeaders)) {
    if (
      key.toLowerCase().includes('authorization') ||
      key.toLowerCase().includes('cookie')
    ) {
      plainHeaders[key] = '[REDACTED]';
    }
  }
  return plainHeaders;
}

// Replace crypto.randomUUID with a fallback for environments where it's not available
function getErrorId() {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  // Fallback: use Math.random and Date.now
  return `req-${Math.random()
    .toString(36)
    .substring(2, 10)}-${Date.now().toString(36)}`;
}

export function getJwtExpiry(
  headers?: RequestInit['headers'],
): string | undefined {
  let token: string | undefined;
  // Extract Authorization header
  if (headers instanceof Headers) {
    token = headers.get('Authorization') ?? undefined;
  } else if (Array.isArray(headers)) {
    for (const [key, value] of headers) {
      if (key.toLowerCase() === 'authorization') {
        token = value;
        break;
      }
    }
  } else if (typeof headers === 'object' && headers) {
    for (const key of Object.keys(headers)) {
      if (key.toLowerCase() === 'authorization') {
        token = (headers as Record<string, string>)[key];
        break;
      }
    }
  }
  if (!token) return undefined;
  // Remove 'Bearer ' prefix if present
  const jwt = token.startsWith('Bearer ') ? token.slice(7) : token;
  const parts = jwt.split('.');
  if (parts.length !== 3) return undefined;
  try {
    const payload = JSON.parse(
      decodeURIComponent(
        atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
          .split('')
          .map(function (c) {
            return `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`;
          })
          .join(''),
      ),
    );
    if (payload.exp) {
      // exp is in seconds since epoch
      const expDate = new Date(payload.exp * 1000);
      return expDate.toISOString();
    }
  } catch (e) {
    // Ignore decoding errors
  }
  return undefined;
}

/**
 * fetchWithTracing wraps fetch and automatically reports errors to Dynatrace RUM (dtrum) if available.
 * Usage: await fetchWithTracing(url, options)
 */
export async function fetchWithTracing(
  input: RequestInfo,
  init?: RequestInit,
  pdpLoggerApi?: PdpLoggerApi,
): Promise<Response> {
  const exp = getJwtExpiry(init?.headers);
  let expDate: Date | undefined;
  if (exp && !isNaN(Date.parse(exp))) {
    expDate = new Date(exp);
  }

  try {
    const response = await fetch(input, init);

    if (!response.ok) {
      // Clone the response so we can read the body for error reporting
      const errorId = getErrorId();
      const errorClone = response.clone();
      const errorText = await errorClone.text();
      if (typeof window !== 'undefined' && window.dtrum?.reportCustomError) {
        window.dtrum.reportCustomError(
          'Fetch failure',
          `Fetch failed: ${response.status} ${errorText} - from url: ${
            typeof input === 'string' ? input : input.url
          }`,
          JSON.stringify({ errorId }),
        );
      }

      // Log to PDP logger backend as well
      if (pdpLoggerApi) {
        const responseDetails = {
          status: response.status,
          statusText: response.statusText,
          url: typeof input === 'string' ? input : input.url,
          errorId,
          responseBody: errorText,
          requestHeaders: sanitizeHeaders(init?.headers),
          requestBody: init?.body,
          input: input,
          tokenExpiry: expDate ? expDate.toUTCString() : undefined, // <-- log expiry
        };

        await pdpLoggerApi.log('error', `Fetch failed`, responseDetails);
      }
    }

    return response;
  } catch (err) {
    // Generate request id for tracing
    const errorId = getErrorId();
    if (typeof window !== 'undefined' && window.dtrum?.reportCustomError) {
      window.dtrum.reportCustomError(
        `Fetch failure`,
        `Fetch threw error: ${
          err instanceof Error ? err.message : String(err)
        } - from url: ${typeof input === 'string' ? input : input.url}`,
        JSON.stringify({ errorId }),
      );
    }
    // Log to PDP logger backend as well
    if (pdpLoggerApi) {
      const errorDetails = {
        errorName: err instanceof Error ? err.name : typeof err,
        errorStack: err instanceof Error ? err.stack : undefined,
        url: typeof input === 'string' ? input : input.url,
        errorId,
        errorMessage: err instanceof Error ? err.message : String(err),
        requestHeaders: sanitizeHeaders(init?.headers),
        requestBody: init?.body,
        input: input,
        tokenExpiry: expDate ? expDate.toUTCString() : undefined, // <-- log expiry
      };

      await pdpLoggerApi.log(
        'error',
        `Fetch threw error: ${
          err instanceof Error ? err.message : String(err)
        } - from url: ${typeof input === 'string' ? input : input.url}`,
        errorDetails,
      );
    }
    throw err;
  }
}

/**
 * fetchWithTracingAndRetries wraps fetchWithTracingCore and retries the request up to `retries` times on error.
 * Only logs and reports to Dynatrace if all retries fail.
 */
export async function fetchWithTracingAndRetries(
  input: RequestInfo,
  init?: RequestInit,
  pdpLoggerApi?: PdpLoggerApi,
  retries: number = 3,
): Promise<Response> {
  let lastError: any;
  let lastNonOkResponse: Response | undefined;
  let lastNonOkBody: string | undefined;
  let expDate: Date | undefined;
  const exp = getJwtExpiry(init?.headers);
  if (exp && !isNaN(Date.parse(exp))) {
    expDate = new Date(exp);
  }
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const response = await fetch(input, init);

      if (!response.ok) {
        lastError = new Error(
          `Non-ok response: ${response.status} ${response.statusText}`,
        );

        lastNonOkResponse = response;

        // Only read body for last attempt (to avoid consuming stream early)
        if (attempt === retries - 1) {
          try {
            lastNonOkBody = await response.clone().text();
          } catch {
            if (!lastNonOkBody) {
              lastNonOkBody = 'Failed to parse response text.';
            }
          }
        }

        if (attempt < retries - 1) {
          await new Promise(res => setTimeout(res, 250 * (attempt + 1)));
          continue;
        } else {
          // On last attempt, return the last non-ok response
          return response;
        }
      }
      // If response is ok, return it
      return response;
    } catch (err) {
      lastError = err;
      // Don't retry if the request was intentionally aborted
      if (init?.signal?.aborted) {
        break;
      }
      if (attempt < retries - 1) {
        await new Promise(res => setTimeout(res, 250 * (attempt + 1)));
      }
    }
  }
  // All retries failed: log/report error or non-ok response
  const errorId = getErrorId();
  if (typeof window !== 'undefined' && window.dtrum?.reportCustomError) {
    if (lastNonOkResponse) {
      window.dtrum.reportCustomError(
        'Fetch failure',
        `Fetch failed after ${retries} retries: ${lastNonOkResponse.status} ${
          lastNonOkBody ?? ''
        } - from url: ${typeof input === 'string' ? input : input.url}`,
        JSON.stringify({ errorId }),
      );
    } else {
      window.dtrum.reportCustomError(
        `Fetch failure`,
        `Fetch threw error after ${retries} retries: ${
          lastError instanceof Error ? lastError.message : String(lastError)
        } - from url: ${typeof input === 'string' ? input : input.url}`,
        JSON.stringify({ errorId }),
      );
    }
  }
  if (pdpLoggerApi) {
    if (lastNonOkResponse) {
      const responseDetails = {
        status: lastNonOkResponse.status,
        statusText: lastNonOkResponse.statusText,
        url: typeof input === 'string' ? input : input.url,
        errorId,
        responseBody: lastNonOkBody,
        requestHeaders: sanitizeHeaders(init?.headers),
        requestBody: init?.body,
        input: input,
        tokenExpiry: expDate ? expDate.toUTCString() : undefined,
        retries,
      };
      await pdpLoggerApi.log(
        'error',
        `Fetch failed after ${retries} retries`,
        responseDetails,
      );
    } else {
      const errorDetails = {
        errorName:
          lastError instanceof Error ? lastError.name : typeof lastError,
        errorStack: lastError instanceof Error ? lastError.stack : undefined,
        url: typeof input === 'string' ? input : input.url,
        errorId,
        errorMessage:
          lastError instanceof Error ? lastError.message : String(lastError),
        requestHeaders: sanitizeHeaders(init?.headers),
        requestBody: init?.body,
        input: input,
        tokenExpiry: expDate ? expDate.toUTCString() : undefined,
        retries,
      };
      await pdpLoggerApi.log(
        'error',
        `Fetch threw error after ${retries} retries: ${
          lastError instanceof Error ? lastError.message : String(lastError)
        } - from url: ${typeof input === 'string' ? input : input.url}`,
        errorDetails,
      );
    }
  }

  if (lastNonOkResponse) {
    return lastNonOkResponse;
  }

  throw lastError;
}
